import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { TransportSchemeDirectionResponse } from '@/api/generated/schemas';
import { TransportMode } from '@/api/generated/schemas';
import { Badge } from '@/shared/ui/badge';
import { Panel } from '@/shared/ui/panel';

interface DirectionPanelProps {
  direction: TransportSchemeDirectionResponse;
  /** Tlačítko přidání dopravy — panel ho jen umístí do hlavičky. */
  action?: ReactNode;
  children: ReactNode;
}

/**
 * Rám jednoho směru. Podtitulek je celé směrování řetězu, badge nese počet chyb.
 */
export function DirectionPanel({ direction, action, children }: DirectionPanelProps) {
  const { t } = useTranslation('venua-planner');

  const isOutbound = direction.direction === 'Outbound';
  const legs = direction.legs;

  const routing =
    legs.length > 0
      ? [legs[0].fromCode, ...legs.map((leg) => leg.toCode)].filter(Boolean).join('–')
      : null;

  const subtitle = routing
    ? `${routing} · ${t('transportScheme.direction.legCount', { count: legs.length })}`
    : t('transportScheme.direction.noLegs');

  return (
    <Panel
      collapsible
      icon={direction.mode === TransportMode.Air ? 'plane' : 'bus'}
      title={t(
        isOutbound ? 'transportScheme.direction.outbound' : 'transportScheme.direction.inbound'
      )}
      subtitle={subtitle}
      actions={
        <div className="flex items-center gap-2">
          {direction.errors.length > 0 && (
            <Badge appearance="soft" tone="danger">
              {t('transportScheme.direction.problems', { count: direction.errors.length })}
            </Badge>
          )}
          {action}
        </div>
      }
    >
      {children}
    </Panel>
  );
}
